<template>
   <div :class="['', departmentClass]">
    <div id="nav-wrapper" :class="{ 'hide-nav': gameStore.gameStarted }">
      <nav class="main-nav" v-show="!gameStore.gameStarted">
        <ul class="nav-list">
          <li class="nav-item">
            <router-link 
              to="/" 
              class="nav-link"
              active-class="active"
              exact
            >
             Choose Game
            </router-link>
          </li>

          <!-- Departments view (commented out for now) -->
          <!-- <li class="nav-item" v-if="navigationFeatures.departmentsView">
            <router-link 
              to="/departments"
              class="nav-link"
              active-class="active"
            >
             Departments
            </router-link>
            </li> -->

          <!-- How To Play - only show if enabled -->
          <li v-if="navigationFeatures.howToPlay">
            <router-link 
              to="/how-to-play" 
              class="nav-link"
              active-class="active"
            >
              How To Play
            </router-link>
          </li>
          
          
          <!-- Player progress - only for email-authenticated players -->
          <li v-if="showMyStats" class="nav-item">
            <router-link
              to="/my-stats"
              class="nav-link"
              active-class="active"
            >
             My Progress
            </router-link>
          </li>

          <!-- Admin Panel - always show -->
          <li class="nav-item">
            <router-link
              to="/manage-cards"
              class="nav-link"
              active-class="active"
            >
             Admin
            </router-link>
          </li>
        </ul>
      </nav>
      
      <h1><strong>OnCall</strong> <span>PRIORITY</span></h1>
      
      <div class="nav-buttons" v-show="!gameStore.gameStarted">
        <button id="modal-button" @click="openSettings">
          <i class="fas fa-gears fa-xl"></i>
        </button>
        <button id="logout-button" @click="logout" v-if="showLogoutButton">
          <i class="fas fa-sign-out-alt fa-lg"></i>
        </button>
      </div>
    </div>




    <router-view />
    <SettingsModal v-model:show="isSettingsModalOpen" />
    <FontSizeManager v-if="isEnabled('FONT_SIZE_ADJUSTER')" />
    <EnvironmentIndicator v-if="isEnabled('ENVIRONMENT_INDICATOR')" />
  </div>
</template>
  
<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import SettingsModal from "@/components/SettingsModal.vue";
import FontSizeManager from "@/components/FontSizeManager.vue";
import EnvironmentIndicator from "@/components/EnvironmentIndicator.vue";
import { useGameStore } from '@/stores/gameStore';
import { useRoute, useRouter } from 'vue-router';
import { supabase } from '@/supabase';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

const gameStore = useGameStore();
const route = useRoute();
const router = useRouter();
const isSettingsModalOpen = ref(false);
const { isEnabled, navigationFeatures } = useFeatureFlags();

// Global PASSWORD_RECOVERY handler — redirects to /reset-password wherever the
// recovery link lands, even if the token was dropped on the wrong page.
let authListener = null;
onMounted(() => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
    if (event === 'PASSWORD_RECOVERY' && route.path !== '/reset-password') {
      router.push('/reset-password');
    }
  });
  authListener = subscription;
});
onUnmounted(() => {
  authListener?.unsubscribe();
});



// Compute a department-specific CSS class from the route parameter (e.g., "department-1")
const departmentClass = computed(() => {
  const depId = route.params.departmentId;
  return depId ? `department-${depId}` : '';
});

const openSettings = () => {
  isSettingsModalOpen.value = true;
};


// Only email-authenticated players have recorded statistics; game-code players
// and admins do not, so the link is hidden rather than leading to an empty page.
const showMyStats = computed(() => {
  if (!isEnabled('USER_STATS')) return false;
  try {
    const raw = localStorage.getItem('gameSession');
    if (!raw) return false;
    return JSON.parse(raw)?.authType === 'email';
  } catch {
    return false;
  }
});

// Show logout button on admin page and game pages (only if auth is enabled)
const showLogoutButton = computed(() => {
  const isAdminRoute = route.path === '/manage-cards' && isEnabled('ADMIN_AUTH');
  const isGameRoute = (route.path.startsWith('/game/') || route.path === '/') && isEnabled('GAME_AUTH');
  return isAdminRoute || isGameRoute;
});

// Unified logout function that handles both admin and game logout
const logout = async () => {
  console.log("App.vue logout called, current route:", route.path);
  
  try {
    // Clear all local storage
    localStorage.removeItem('gameSession');
    localStorage.removeItem('gameCode');
    localStorage.removeItem('session');
    localStorage.removeItem('loggedIn');
    localStorage.removeItem('userDeptAccess');
    
    // Sign out from Supabase
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Supabase logout error:', error);
    }
    
    // Redirect based on current route and feature flags
    if (route.path === '/manage-cards' && isEnabled('ADMIN_AUTH')) {
      // Admin logout - emit event and let CardManagement handle the logout process
      console.log("Emitting logout-requested event for admin logout");
      window.dispatchEvent(new CustomEvent('logout-requested'));
      // Don't redirect immediately - let CardManagement handle the redirect after logout
    } else if ((route.path.startsWith('/game/') || route.path === '/') && isEnabled('GAME_AUTH')) {
      // Game logout - redirect to game login immediately
      console.log("Redirecting to game login for game logout");
      window.location.href = '/game-login';
    } else {
      // Fallback redirect if auth is disabled
      console.log("Auth disabled, redirecting to home");
      window.location.href = '/';
    }
  } catch (error) {
    console.error('Logout error:', error);
    // Force redirect even if there's an error
    window.location.href = '/game-login';
  }
};

</script>
  
